import { Injectable, NgZone, inject } from '@angular/core';
import type { ApprovalAsk, ApprovalAnswer, ZavoBridge } from '../../services/mcp/mcp-types';

/** The part of the desktop app's `window.zavoBridge` the approval layer uses. */
export type ApprovalBridge = Pick<ZavoBridge, 'onApprovalAsk' | 'answerApproval' | 'onApprovalCancel'>;

export interface ApprovalHistoryEntry {
  ask: ApprovalAsk;
  answer: ApprovalAnswer | 'dropped';
  timestamp: number;
}

const APPROVAL_TIMEOUT_MS = 5 * 60 * 1000;
/** Long enough that a click aimed at one card can't land on the card that replaces it. */
const ARM_DELAY_MS = 1000;
const MAX_HISTORY = 50;

interface PendingAsk {
  ask: ApprovalAsk;
  /** Main's id for an ask it raised, so main can end it by name. */
  askId: string | null;
  deadline: number;
  timer: ReturnType<typeof setTimeout>;
  resolve: (answer: ApprovalAnswer) => void;
}

/**
 * One card at a time, in the order asks arrive. A new ask never replaces the
 * open one: it waits, and its own time limit runs from when it arrived, so an
 * ask that waits too long ends expired just as a shown one would.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalService {
  currentAsk: ApprovalAsk | null = null;
  history: ApprovalHistoryEntry[] = [];
  remainingSeconds = 0;
  /** Asks queued behind the open one. */
  waitingCount = 0;
  /** False for a moment after the card's content changes; the buttons ignore clicks until then. */
  buttonsArmed = false;

  private zone = inject(NgZone);
  private queue: PendingAsk[] = [];
  private armedAt = 0;
  private armTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ask(ask: ApprovalAsk, askId: string | null = null): Promise<ApprovalAnswer> {
    return this.zone.run(() => new Promise<ApprovalAnswer>((resolve) => {
      const pending: PendingAsk = {
        ask,
        askId,
        deadline: Date.now() + APPROVAL_TIMEOUT_MS,
        resolve,
        timer: setTimeout(() => this.zone.run(() => this.end(pending, 'expired')), APPROVAL_TIMEOUT_MS),
      };
      this.queue.push(pending);
      this.refresh();
    }));
  }

  /** The open card's answer, from code rather than a click. */
  answer(answer: ApprovalAnswer): void {
    this.zone.run(() => {
      if (this.queue.length) this.end(this.queue[0], answer);
    });
  }

  /** A click on the card. Ignored, returning false, while the card has only just appeared. */
  choose(answer: 'approved' | 'declined'): boolean {
    if (!this.queue.length || Date.now() < this.armedAt) return false;
    this.answer(answer);
    return true;
  }

  /** Main stopped waiting for one of its own asks; it ends expired wherever it is in the queue. */
  cancelAsk(askId: string): void {
    this.zone.run(() => {
      const pending = this.queue.find((p) => p.askId === askId);
      if (pending) this.end(pending, 'dropped');
    });
  }

  /**
   * Main dropped the connection, so every held and data ask ends expired. A
   * connect ask belongs to main, which ends it by id, so it stays.
   */
  cancelConnectionAsks(): void {
    this.zone.run(() => {
      for (const pending of this.queue.filter((p) => p.ask.kind !== 'connect')) this.end(pending, 'dropped');
    });
  }

  formatCountdown(): string {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = this.remainingSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private end(pending: PendingAsk, outcome: ApprovalAnswer | 'dropped'): void {
    const index = this.queue.indexOf(pending);
    if (index < 0) return;
    this.queue.splice(index, 1);
    clearTimeout(pending.timer);
    this.history.unshift({ ask: pending.ask, answer: outcome, timestamp: Date.now() });
    if (this.history.length > MAX_HISTORY) this.history.length = MAX_HISTORY;
    pending.resolve(outcome === 'dropped' ? 'expired' : outcome);
    this.refresh();
  }

  /** Brings the card, the countdown and the button guard in line with the head of the queue. */
  private refresh(): void {
    const head = this.queue[0] ?? null;
    const changed = head?.ask !== this.currentAsk;
    this.currentAsk = head?.ask ?? null;
    this.waitingCount = Math.max(0, this.queue.length - 1);

    if (!head) {
      this.stopTimers();
      this.remainingSeconds = 0;
      this.buttonsArmed = false;
      return;
    }

    this.updateRemaining();
    if (this.countdownInterval === null) {
      this.countdownInterval = setInterval(() => this.zone.run(() => this.updateRemaining()), 1000);
    }
    if (changed) this.arm();
  }

  private arm(): void {
    this.buttonsArmed = false;
    this.armedAt = Date.now() + ARM_DELAY_MS;
    if (this.armTimer !== null) clearTimeout(this.armTimer);
    this.armTimer = setTimeout(() => this.zone.run(() => {
      this.armTimer = null;
      this.buttonsArmed = this.queue.length > 0;
    }), ARM_DELAY_MS);
  }

  private updateRemaining(): void {
    const head = this.queue[0];
    this.remainingSeconds = head ? Math.ceil(Math.max(0, head.deadline - Date.now()) / 1000) : 0;
  }

  private stopTimers(): void {
    if (this.armTimer !== null) { clearTimeout(this.armTimer); this.armTimer = null; }
    if (this.countdownInterval !== null) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
  }
}
